import {
  getDefaultPresetTool,
  getPhotoEditorJtbdCategories,
  getPhotoEditorPreviewPoster,
  getToolsForCategory,
  GO_VIRAL_AVATAR_STYLE_COUNT,
  GO_VIRAL_AVATARS_HREF,
  GO_VIRAL_CATEGORY_ID,
  photoEditorCatalog,
  type PhotoEditorCategory,
  type PhotoEditorTool,
} from "@/app/editor/data/ai-photo-editor";
import {
  getHubBrandingLookCards,
  getHubFreeToolCards,
  getHubHeadshotLookCards,
  getPhotoEditorGoViralAvatarStyles,
} from "@/components/dashboard/jtbd/jtbdData";
import {
  JTBD_AVATAR_HERO_IMAGES,
  JTBD_HEADSHOT_HERO_IMAGES,
} from "@/components/dashboard/jtbd/jtbdHeroMedia";

export const HEADSHOTS_PILLAR_ID = "headshots";
export const AI_PHOTOSHOOT_PILLAR_ID = "ai-photoshoot";
export const PHOTO_EDITOR_PILLAR_ID = "photo-editor";
export const FREE_TOOLS_PILLAR_ID = "free-pfpmaker";

/** 2×6 mobile, 4×3 md, 6×2 desktop. */
export const HUB_LOOK_CARD_COUNT = 12;
/** 1 row × 6 cols on desktop. */
export const HUB_FREE_CARD_COUNT = 6;

export type HubNavItem = {
  id: string;
  label: string;
  kind: "pillar" | "jtbd";
  scrollId: string;
  thumb?: string;
  emoji?: string;
};

export type HubBannerTone = "headshots" | "photoshoot" | "editor" | "free";

export const HUB_BANNER_TONES: Record<HubBannerTone, { fill: string; r: number; g: number; b: number }> = {
  headshots: { fill: "#173A6E", r: 23, g: 58, b: 110 },
  photoshoot: { fill: "#C4785A", r: 196, g: 120, b: 90 },
  editor: { fill: "#5956E9", r: 89, g: 86, b: 233 },
  free: { fill: "#69C85A", r: 105, g: 200, b: 90 },
};

export type HubBanner = {
  label: string;
  headline: string;
  subhead?: string;
  images: string[];
  trackingToolId: string;
  trackingCardId: string;
  tone: HubBannerTone;
};

export type HubLookCard = {
  type: "look";
  id: string;
  title: string;
  tagline?: string;
  images: string[];
  href: string;
  toolId: string;
  cardType: "look" | "style";
  overlaySrc?: string;
  borderFrames?: Array<{ color: string; bg: string }>;
};

export type HubToolCard = {
  type: "tool";
  tool: PhotoEditorTool;
};

export type HubCard = HubLookCard | HubToolCard;

export type HubLooksSection = {
  kind: "looks";
  id: string;
  banner: HubBanner;
  cards: HubLookCard[];
  viewAllHref: string;
  viewAllLabel: string;
  /** Omit from top nav; still scrollable. */
  stealth?: boolean;
  /** Desktop is a single 6-card row (no 2-row visibility clipping). */
  oneRow?: boolean;
};

export type HubEditorSection = {
  kind: "editor";
  id: string;
  banner: HubBanner;
};

export type HubJtbdSection = {
  kind: "jtbd";
  id: string;
  title: string;
  tagline?: string;
  emoji?: string;
  cards: HubCard[];
  viewAllHref?: string;
  viewAllLabel?: string;
};

export type HubSection = HubLooksSection | HubEditorSection | HubJtbdSection;

function jtbdCards(category: PhotoEditorCategory): HubCard[] {
  if (category.id === GO_VIRAL_CATEGORY_ID) {
    return getPhotoEditorGoViralAvatarStyles(GO_VIRAL_AVATAR_STYLE_COUNT).map((style) => ({
      type: "look",
      id: style.id,
      title: style.title,
      images: [style.image],
      href: `${GO_VIRAL_AVATARS_HREF}?style=${encodeURIComponent(style.id)}`,
      toolId: "ai-avatars",
      cardType: "style",
    }));
  }
  return getToolsForCategory(category.id).map((tool) => ({ type: "tool", tool }));
}

function headshotsSection(): HubLooksSection {
  const looks = getHubHeadshotLookCards().slice(0, HUB_LOOK_CARD_COUNT);
  return {
    kind: "looks",
    id: HEADSHOTS_PILLAR_ID,
    banner: {
      label: "AI Headshots",
      headline: "Professional headshots, studio-quality.",
      subhead: "515+ outfits, backgrounds, and poses - pick a look and generate.",
      images: JTBD_HEADSHOT_HERO_IMAGES,
      trackingToolId: "ai-headshots",
      trackingCardId: HEADSHOTS_PILLAR_ID,
      tone: "headshots",
    },
    cards: looks.map((look) => ({
      type: "look",
      id: look.id,
      title: look.title,
      images: look.images,
      href: look.href,
      toolId: "ai-headshots",
      cardType: "look",
    })),
    viewAllHref: "/editor-v2/ai-headshots",
    viewAllLabel: "View all headshot looks",
  };
}

function aiPhotoshootSection(): HubLooksSection {
  const looks = getHubBrandingLookCards(HUB_LOOK_CARD_COUNT);
  return {
    kind: "looks",
    id: AI_PHOTOSHOOT_PILLAR_ID,
    banner: {
      label: "AI Photoshoot",
      headline: "Lifestyle photos for dating, work, and everyday.",
      subhead: "Business-casual Instagram looks from one selfie - get studio quality photos without the prep.",
      images: JTBD_AVATAR_HERO_IMAGES,
      trackingToolId: "ai-avatars",
      trackingCardId: AI_PHOTOSHOOT_PILLAR_ID,
      tone: "photoshoot",
    },
    cards: looks.map((look) => ({
      type: "look",
      id: look.id,
      title: look.title,
      images: look.images,
      href: look.href,
      toolId: "ai-avatars",
      cardType: "style",
    })),
    viewAllHref: "/editor-v2/ai-avatars",
    viewAllLabel: "View all photoshoot looks",
  };
}

function photoEditorSection(): HubEditorSection {
  const poster = getPhotoEditorPreviewPoster(getDefaultPresetTool().preview);
  return {
    kind: "editor",
    id: PHOTO_EDITOR_PILLAR_ID,
    banner: {
      label: photoEditorCatalog.title,
      headline: "Every portrait tool, one studio.",
      subhead: "Backgrounds, beauty, try-on, and filters - pick a tool below.",
      images: poster ? [poster] : [],
      trackingToolId: "ai-photo-editor",
      trackingCardId: PHOTO_EDITOR_PILLAR_ID,
      tone: "editor",
    },
  };
}

function freeToolsSection(): HubLooksSection {
  const cards = getHubFreeToolCards().slice(0, HUB_FREE_CARD_COUNT);
  const bannerImages =
    cards.find((card) => card.id === "creative-backgrounds")?.images ??
    cards.find((card) => card.images.some((src) => !src.startsWith("data:")))?.images ??
    [];
  return {
    kind: "looks",
    id: FREE_TOOLS_PILLAR_ID,
    oneRow: true,
    banner: {
      label: "Free Tools",
      headline: "Classic PFPMaker tools, free.",
      subhead: "Remove background, add a new one, add border and text - no credits needed.",
      images: bannerImages,
      trackingToolId: "remove-background",
      trackingCardId: FREE_TOOLS_PILLAR_ID,
      tone: "free",
    },
    cards: cards.map((card) => ({
      type: "look",
      id: card.id,
      title: card.title,
      images: card.images,
      href: card.href,
      toolId: card.toolId,
      cardType: "style",
      overlaySrc: card.overlaySrc,
      borderFrames: card.borderFrames,
    })),
    viewAllHref: "/pfpmaker-editor/remove-background",
    viewAllLabel: "Open free editor",
  };
}

function jtbdSection(category: PhotoEditorCategory): HubJtbdSection {
  return {
    kind: "jtbd",
    id: category.id,
    title: category.title.replace(/ Tools$/, ""),
    tagline: category.tagline,
    emoji: category.emoji,
    cards: jtbdCards(category),
    viewAllHref: category.id === GO_VIRAL_CATEGORY_ID ? GO_VIRAL_AVATARS_HREF : undefined,
    viewAllLabel: category.id === GO_VIRAL_CATEGORY_ID ? "Show all →" : undefined,
  };
}

export function getHubSections(): HubSection[] {
  return [
    headshotsSection(),
    aiPhotoshootSection(),
    freeToolsSection(),
    photoEditorSection(),
    ...getPhotoEditorJtbdCategories().map(jtbdSection),
  ];
}

export function getHubNav(sections: HubSection[]): { pillars: HubNavItem[]; jtbds: HubNavItem[] } {
  const looks = sections.filter(
    (section): section is HubLooksSection => section.kind === "looks" && !section.stealth
  );
  const editor = sections.find((section): section is HubEditorSection => section.kind === "editor");
  const freeTools = sections.find(
    (section): section is HubLooksSection =>
      section.kind === "looks" && section.id === FREE_TOOLS_PILLAR_ID
  );
  const jtbds = sections.filter((section): section is HubJtbdSection => section.kind === "jtbd");

  const pillars: HubNavItem[] = [
    {
      id: HEADSHOTS_PILLAR_ID,
      label: "AI Headshots",
      kind: "pillar",
      scrollId: HEADSHOTS_PILLAR_ID,
      thumb: looks.find((section) => section.id === HEADSHOTS_PILLAR_ID)?.banner.images[0],
    },
    {
      id: AI_PHOTOSHOOT_PILLAR_ID,
      label: "AI Photoshoot",
      kind: "pillar",
      scrollId: AI_PHOTOSHOOT_PILLAR_ID,
      thumb: looks.find((section) => section.id === AI_PHOTOSHOOT_PILLAR_ID)?.banner.images[0],
    },
    {
      id: PHOTO_EDITOR_PILLAR_ID,
      label: "Photo Editor",
      kind: "pillar",
      scrollId: PHOTO_EDITOR_PILLAR_ID,
      thumb: editor?.banner.images[0],
    },
    {
      id: FREE_TOOLS_PILLAR_ID,
      label: "PFP Maker",
      kind: "pillar",
      scrollId: FREE_TOOLS_PILLAR_ID,
      thumb: freeTools?.banner.images[0],
    },
  ];

  return {
    pillars,
    jtbds: jtbds.map((section) => ({
      id: section.id,
      label: section.title,
      kind: "jtbd" as const,
      scrollId: section.id,
      emoji: section.emoji,
    })),
  };
}

export function isHubSectionId(id: string, sections: HubSection[]): boolean {
  return sections.some((section) => section.id === id);
}

export function isPhotoEditorJtbdId(id: string): boolean {
  return getPhotoEditorJtbdCategories().some((category) => category.id === id);
}

export function isFreeToolsPillarId(id: string): boolean {
  return id === FREE_TOOLS_PILLAR_ID;
}

export function resolveHubScrollId(id: string, nav: { pillars: HubNavItem[]; jtbds: HubNavItem[] }): string {
  const hit = [...nav.pillars, ...nav.jtbds].find((item) => item.id === id);
  return hit?.scrollId ?? id;
}

/** Legacy dashboard hashes → current section ids. */
const HUB_HASH_ALIASES: Record<string, string> = {
  brand: "branding",
  "free-tools": FREE_TOOLS_PILLAR_ID,
};

export function normalizeHubHash(raw: string): string {
  const id = raw.trim();
  return HUB_HASH_ALIASES[id] ?? id;
}

export function hubCardKey(card: HubCard, sectionId: string): string {
  return card.type === "tool" ? `${sectionId}-${card.tool.id}` : `${sectionId}-${card.id}`;
}
